/**
 * universeTaxonomy — the DISPLAY grouping over the brand universe.
 *
 * A read-time projection. The graph endpoint hands the browser every fact, every
 * concept and every fact-to-concept membership, so this regroups data already in
 * memory and writes nothing back.
 *
 * TWO RULES, and everything else follows.
 *
 * 1. A FACET IS A CONSEQUENCE OF WHAT THE SUBJECT IS. There is no vocabulary that
 *    applies to all five. An organisation has identity and behaviour. People have
 *    composition, drive and behaviour. A market has a boundary, a convention and a
 *    direction. A context has a calendar and a conversation.
 *
 *    An earlier version applied ONE shared facet list to brand, competitors and
 *    audience. Wrong in product terms: an audience segment has no mission and no
 *    "never do". Wrong in mechanism too: BrandUniverseConcept is
 *    @@unique([brandId, name]), so there is exactly one "Mission" row per brand and
 *    audience.mission and brand.mission resolved to the IDENTICAL concept id. They
 *    were aliases, not namespaces, and the audience subject was literally showing
 *    the brand's own drawers.
 *
 *    The trap: three of those facets RHYME with real persona fields while inverting
 *    the referent. digital_home is channels-shaped but means where THEY are, not
 *    where WE appear. how_we_speak is voice-shaped but means the register WE use
 *    toward them. avoids means what turns the PERSON off, not what the brand
 *    refuses to do. Same shape, opposite subject.
 *
 * 2. THE UNIVERSE HOLDS KNOWLEDGE, NOT ANALYTICS. Emad, 2026-08-31: "the engagement,
 *    hooks are analytics, I don't want them in the universe. I wanna know who are the
 *    competitors, direct vs indirect, their campaigns whenever they get accepted to
 *    the universe, anything about them. Analytics will be part of the competitor
 *    analysis."
 *
 *    So: a named campaign is a durable fact and belongs here. Its engagement rate is
 *    a measurement and belongs in the strategy document's content_analysis, which is
 *    dated, re-measured, and already designed for it. The test is whether the claim
 *    is still true in a year. "They run a weekly founder series" survives. "They
 *    average 4.2% engagement" does not.
 *
 * 3. CULTURE ARRIVES BY GRADUATION, NOT BY RESEARCH. Emad, 2026-08-31: "culture
 *    will not only be cultural moments, but any cultural shifts that happen that we
 *    observe in signals and want to include in the universe."
 *
 *    That is the shipped architecture, not a new idea. BrandSignal is the
 *    observation stream: it has an expiry, a curation queue, and a `graduated`
 *    status whose `graduatedFactId` points at the permanent fact it became
 *    (POST /:brandId/:signalId/graduate -> GraduateSignalUseCase ->
 *    BrandUniverseWriter.graduateSignal). Signals decay; the universe keeps what
 *    someone decided was worth keeping.
 *
 *    So the Culture drawer is "Shifts", not "what's spreading". Something spreading
 *    is still a signal and should be allowed to expire. Something that SHIFTED is a
 *    fact, and that is what gets promoted.
 *
 *    Design consequence: this taxonomy is also the DESTINATION MAP for graduated
 *    signals. Every SignalKind needs an obvious drawer or graduation drops facts
 *    into Unsorted. Current mapping, for whoever wires it:
 *      cultural_moment   -> Culture / Moments
 *      trend             -> Culture / Shifts   (once it has stopped being a trend)
 *      category_shift    -> Category / Where it's heading
 *      audience_shift    -> Audience / What drives them
 *      positioning_shift -> Brand or Competitors / positioning, per its subject
 *      tone_shift        -> Brand / Voice, or Competitors / What they stand for
 *      platform_shift    -> Brand / Where we show up
 *      competitive_pattern, active_campaign -> Campaigns & work
 *      identity          -> Brand / Mission or Positioning
 *    The ambiguous ones (tone_shift, positioning_shift) need the subject marker to
 *    disambiguate, which the anchor rule below already provides.
 *
 * 4. THE SUBJECT MARKER IS NEVER A DRAWER. "Competitors" tags a fact as being
 *    ABOUT them. A marker used as a facet swallows its whole subject, because the
 *    rail fills drawers by membership. Marker-only facts sit on the subject row as
 *    Unfiled. Audience has no marker: the backend's concept named "Audience"
 *    holds who they are, so it is that subject's first drawer.
 *
 * The vocabulary below is NOT invented. It mirrors schemas this product already
 * shipped for these five subjects: StrategyPersona (demographics, core_motivation,
 * traits, seeks, avoids, digital_home, how_we_speak), CategoryLandscape,
 * CompetitorProfile, BrandAudit, CulturalMoment, TrendLandscape and
 * UgcSocialListening. Derivation: mohtawa/creative-os/BRAND-UNIVERSE-IA.md.
 */

/** A leaf. `concept` MUST be the backend's literal concept name. A facet whose
 *  concept is not seeded yet moves to `missingFacets` at resolve time, so this
 *  table can state the target vocabulary before the backend has it. */
export type Facet = {
  key: string;
  label: string;
  concept: string;
  blurb: string;
};

export type Subject = {
  key: string;
  label: string;
  blurb: string;
  status: "live" | "planned";
  /** Marks a fact as belonging to this subject whatever else it carries. NEVER
   *  also listed as a facet. Brand has none: it is the named default below. */
  anchorConcept?: string;
  facets: Facet[];
};

/**
 * BRAND is the default subject: a fact tagged only with facet concepts is about us.
 * Named explicitly, because "first subject with facets" breaks the moment CATEGORY
 * is seeded - it sits first in the owner's order and would hijack every untagged
 * brand fact.
 */
export const DEFAULT_SUBJECT_KEY = "brand";

/** Emad's order, for the list, the rings (inner to outer) and the phone chips:
 *  category, audience, brand, competitors, culture. The default subject is
 *  resolved by KEY (DEFAULT_SUBJECT_KEY), so this order never decides where an
 *  untagged fact falls. */
export const UNIVERSE_TAXONOMY: Subject[] = [
  {
    key: "category",
    label: "CATEGORY",
    blurb: "The market we compete in.",
    status: "planned",
    facets: [
      { key: "category.field", label: "The field", concept: "Category", blurb: "Boundary, scale, growth, who leads." },
      { key: "category.convention", label: "What everyone does the same", concept: "Category Convention", blurb: "Shared claims and formats you could swap logos on." },
      { key: "category.shift", label: "Where it's heading", concept: "Category Shift", blurb: "Structural movement, with a direction." },
    ],
  },
  {
    key: "audience",
    label: "AUDIENCE",
    blurb: "Who we are talking to.",
    status: "live",
    // No anchor. The backend names its "who they are" concept literally
    // "Audience", so that concept is the first drawer here rather than a subject
    // marker. Audience facts reach this lane through step 4 of the layout (the
    // first subject whose facets match), never through an anchor.
    facets: [
      { key: "audience.demographics", label: "Who they are", concept: "Audience", blurb: "Age, life stage, location, income, household, size." },
      { key: "audience.psychographics", label: "What drives them", concept: "Psychographics", blurb: "Motivations, values, identity, aspirations, anxieties." },
      { key: "audience.wants", label: "What they want and reject", concept: "Preferences", blurb: "Proof they look for; what makes them distrust or scroll." },
      { key: "audience.behaviour", label: "How they behave", concept: "Audience Behavior", blurb: "Platforms and formats they use; how they discover and buy." },
    ],
  },
  {
    key: "brand",
    label: "BRAND",
    blurb: "Who we are and how we behave.",
    status: "live",
    facets: [
      { key: "brand.mission", label: "Mission & vision", concept: "Mission", blurb: "What we are for." },
      { key: "brand.positioning", label: "Positioning", concept: "Positioning", blurb: "The claim only we can make, and who we are not for." },
      { key: "brand.offer", label: "Offer & proof", concept: "Offer", blurb: "What we sell and the evidence behind it." },
      { key: "brand.personality", label: "Personality", concept: "Personality", blurb: "What we are like as a character." },
      { key: "brand.voice", label: "Voice & tone", concept: "Voice", blurb: "How we sound." },
      { key: "brand.visual", label: "Visual world", concept: "Visual", blurb: "How we look." },
      { key: "brand.channels", label: "Where we show up", concept: "Channels", blurb: "Our own surfaces and touchpoints." },
      { key: "brand.work", label: "Campaigns & work", concept: "Campaigns", blurb: "Named campaigns, activations, partnerships and series we have run." },
      { key: "brand.avoid", label: "Never do", concept: "Avoid", blurb: "Lines we will not cross." },
    ],
  },
  {
    key: "competitors",
    label: "COMPETITORS",
    blurb: "Who else is in the room.",
    status: "live",
    anchorConcept: "Competitors",
    facets: [
      { key: "competitors.positioning", label: "What they stand for", concept: "Rival Positioning", blurb: "The territory they claim and the argument they make." },
      { key: "competitors.offer", label: "What they sell", concept: "Rival Offer", blurb: "Their products, range and price position." },
      { key: "competitors.channels", label: "Where they show up", concept: "Rival Channels", blurb: "The platforms and handles they run. Not how often they post." },
      // The ONE genuinely shared facet: brand and competitors are both organisations
      // that run campaigns, and a campaign is a durable thing that happened.
      { key: "competitors.work", label: "Campaigns & work", concept: "Campaigns", blurb: "Named campaigns, activations, partnerships and series they have run." },
      // "Where they're soft" was here and is deliberately gone. A gap is an
      // INFERENCE someone drew, not a fact about a competitor, so it breaks the
      // knowledge-not-analytics rule the same way engagement rates did. Whitespace
      // belongs in competitor_landscape, beside the analysis that produced it.
    ],
  },
  {
    key: "culture",
    label: "CULTURE",
    blurb: "The world our audience lives in.",
    status: "planned",
    facets: [
      { key: "culture.moments", label: "Moments", concept: "Cultural Moment", blurb: "Dated occasions the audience already observes." },
      { key: "culture.shifts", label: "Shifts", concept: "Cultural Shift", blurb: "A change in what people do or believe that proved durable enough to keep." },
      { key: "culture.conversation", label: "What people are saying", concept: "Public Conversation", blurb: "Unprompted talk about the category, us, or rivals." },
    ],
  },
];

export const OTHER_SUBJECT_KEY = "other";
/** Facts with zero memberships. Not a taxonomy entry; the layout appends it. */
export const UNSORTED_KEY = "unsorted";

export type ResolvedFacet = Facet & { conceptId: string };
export type ResolvedSubject = Omit<Subject, "facets"> & {
  facets: ResolvedFacet[];
  anchorConceptId?: string;
  /** True when the subject can carry facts: a resolved facet, or an anchor. */
  hasConcepts: boolean;
  /** Named here but not seeded on the backend yet. Shown as stated intent. */
  missingFacets: Facet[];
};

const norm = (s: string) => s.trim().toUpperCase();

/**
 * Resolve the table against a live graph. A facet whose concept the backend has
 * not seeded moves to `missingFacets` rather than being dropped, so the outline
 * states the target shape instead of quietly hiding four fifths of it.
 */
export function resolveTaxonomy(
  concepts: Array<{ id: string; name: string; isSystem: boolean; description?: string | null }>,
): ResolvedSubject[] {
  const system = concepts.filter((c) => c.isSystem);
  const pool = system.length ? system : concepts;
  const byName = new Map(pool.map((c) => [norm(c.name), c]));
  const used = new Set<string>();

  const subjects: ResolvedSubject[] = UNIVERSE_TAXONOMY.map((s) => {
    const facets: ResolvedFacet[] = [];
    const missingFacets: Facet[] = [];
    for (const f of s.facets) {
      const c = byName.get(norm(f.concept));
      if (c) {
        used.add(c.id);
        facets.push({ ...f, conceptId: c.id });
      } else {
        missingFacets.push(f);
      }
    }
    const anchor = s.anchorConcept ? byName.get(norm(s.anchorConcept)) : undefined;
    if (anchor) used.add(anchor.id);
    return {
      ...s,
      facets,
      missingFacets,
      anchorConceptId: anchor?.id,
      hasConcepts: facets.length > 0 || Boolean(anchor),
    };
  });

  const orphans = pool.filter((c) => !used.has(c.id));
  if (orphans.length) {
    subjects.push({
      key: OTHER_SUBJECT_KEY,
      label: "OTHER",
      blurb: "Not yet placed in the taxonomy.",
      status: "live",
      hasConcepts: true,
      missingFacets: [],
      facets: orphans.map((c) => ({
        key: `${OTHER_SUBJECT_KEY}.${c.id}`,
        label: c.name,
        concept: c.name,
        blurb: c.description ?? "",
        conceptId: c.id,
      })),
    });
    if (import.meta.env.DEV) {
      console.warn(
        `[universeTaxonomy] ${orphans.length} system concept(s) not in the display taxonomy: ` +
          orphans.map((c) => c.name).join(", "),
      );
    }
  }
  return subjects;
}

/** Subjects that can actually hold facts - the ones that become orbit rings. */
export function ringSubjects(resolved: ResolvedSubject[]): ResolvedSubject[] {
  return resolved.filter((s) => s.hasConcepts);
}
